import {
  AddIcon,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useBoolean,
  useDisclosure,
  InputGroup,
  InputLeftAddon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Person, usePeople } from "../data/people";
import { usePixelGrid } from "./PixelGrid";

export const EditPeopleModal = () => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { reset } = usePixelGrid();

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onToggle();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const { people } = usePeople();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent fontFamily="Menlo, mono">
        <ModalHeader>Edit People ({people.length})</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack maxH={80} overflowY="auto" p={1}>
            <AddPersonForm />
            {people
              .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
              .map((person, index) => (
                <PersonRowForm key={person.id} person={person} index={index} />
              ))}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup size="sm">
            <Button onClick={reset}>Reset Pixels</Button>
            <ResetHasGoneButton />
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ResetHasGoneButton = () => {
  const { updateAllPeople } = usePeople();
  const [isResetting, setIsResetting] = useBoolean(false);

  return (
    <Button
      onClick={async () => {
        setIsResetting.on();
        try {
          await updateAllPeople({
            hasGone: false,
          });
        } finally {
          setIsResetting.off();
        }
      }}
      isLoading={isResetting}
    >
      RESET THE HAS GONES
    </Button>
  );
};

const AddPersonForm = () => {
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useBoolean(false);
  const { addPerson } = usePeople();

  const handleSave: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setIsSaving.on();
    setName("");
    try {
      await addPerson({
        name: name.toLowerCase(),
        hasGone: false,
      });
    } finally {
      setIsSaving.off();
    }
  };

  return (
    <form onSubmit={handleSave}>
      <Flex pr={1} gap={2}>
        <InputGroup size="sm">
          <InputLeftAddon children="Add Person" fontSize={10} />
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </InputGroup>
        <IconButton
          size="sm"
          type="submit"
          aria-label="Save"
          icon={<AddIcon />}
          isLoading={isSaving}
        />
      </Flex>
    </form>
  );
};

type PersonRowFormProps = {
  index: number;
  person: Person;
};
const PersonRowForm = ({ person, index }: PersonRowFormProps) => {
  const [name, setName] = useState(person.name);
  const [isEditMode, setIsEditMode] = useBoolean(false);
  const { updatePerson } = usePeople();
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (isEditMode) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      setName(person.name);
    }
  }, [isEditMode]);

  const [isSaving, setIsSaving] = useState(false);
  const handleSave: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await updatePerson(person.id, {
        name,
      });
    } finally {
      setIsSaving(false);
      setIsEditMode.off();
    }
  };

  return (
    <form onSubmit={handleSave}>
      <Flex pr={1} gap={2}>
        <InputGroup size="sm">
          <InputLeftAddon children={index} fontSize={10} />
          <Input
            ref={inputRef}
            disabled={!isEditMode}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </InputGroup>
        {isEditMode ? (
          <>
            <IconButton
              size="sm"
              type="submit"
              aria-label="Save"
              isLoading={isSaving}
              icon={<CheckIcon />}
            />
            <IconButton
              size="sm"
              type="button"
              aria-label="cancel"
              icon={<CloseIcon />}
              disabled={isSaving}
              onClick={(e) => {
                e.preventDefault();
                setIsEditMode.off();
              }}
            />
          </>
        ) : (
          <>
            <IconButton
              type="button"
              size="sm"
              aria-label="edit"
              icon={<EditIcon />}
              onClick={(e) => {
                e.preventDefault();
                setIsEditMode.on();
              }}
            />
            <PersonDeleteButton person={person} />
          </>
        )}
      </Flex>
    </form>
  );
};

type PersonDeleteButtonProps = {
  person: Person;
};
const PersonDeleteButton = ({ person }: PersonDeleteButtonProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = () => setIsOpen(false);
  const cancelRef = useRef();
  const { removePerson } = usePeople();

  const [isDeleting, setIsDeleting] = useBoolean(false);
  const handleRemove = async () => {
    setIsDeleting.on();
    try {
      await removePerson(person.id);
    } catch {
      setIsDeleting.off();
    }
  };

  return (
    <>
      <IconButton
        type="button"
        size="sm"
        aria-label="delete"
        icon={<DeleteIcon />}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
      />
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent fontFamily="Menlo, mono">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Person
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove <b>{person.name}</b>?
            </AlertDialogBody>

            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} onClick={onClose} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                isLoading={isDeleting}
                colorScheme="red"
                onClick={handleRemove}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
